import { useEffect, useMemo, useReducer, useRef, type ReactElement } from "react";
import { creatorOffer, creatorStart } from "odyssey-shared";
import "./select-screen.css";
import { say } from "../words.ts";
import { changeLook, chooseCharacter, createCharacter, logoutEverywhere, storeGrant, type Roster, type Session } from "./auth.ts";
import { pageSound } from "./audio/page-sound.ts";
import type { Ask } from "./ask.ts";
import type { ConfirmLogoutEverywhere } from "./logout-everywhere.tsx";
import { logOutWithRetry, type RetryLogout } from "./logout-retry.tsx";
import { chooseAfterOwnLeave, ownLeaveAt } from "./own-leave.ts";
import { useSharedImages } from "./look-preview/standing-look.tsx";
import { pageSeed } from "./look-preview/look-panel.tsx";
import { LookStep, lookStepWords, useLookSource, type EntryLooks } from "./look-step.tsx";
import { lookRefusal } from "./menu/look-screen.ts";
import { SelectScreen, logOutQuestion } from "./select-screen.tsx";
import { changeDiffers, characterLabel, chosenCharacter, firstNameRefused, initialSelectState, selectReducer, type SelectState } from "./select-state.ts";

/**
 * Character select's requests (P1.4-T6 and P1.4-T28, ADR-065 decisions 5 and 6): the screen the login flow
 * shows once a login is answered with the account's characters, before any world loads. Choosing one is a
 * refresh that names it by id, which mints the session token for that character; the page keeps the id with
 * the session, which it stores only once a character is chosen, and names it in every refresh after, so a
 * reload plays the same character. While the account holds fewer characters than its limit, Create a
 * character asks for a first name, then the Look step with the voice (ADR-094 decision 23), whose Enter the world
 * posts the first name, the voice and the look to the route of its own, whose answer is the session for the new
 * character, which the page plays as it plays a chosen one. Change look (decision 29 and note 128) posts the
 * selected character's look and voice to the change of look's route and, once accepted, lists the character in
 * them; it is free and repeatable while the change of look is (ADR-094's amendment B3). Each row draws its
 * character's standing look (decision 27). Log out ends the session and Log out everywhere every session
 * of the account, each returning to the log in form, as the name step's links do.
 *
 * `SelectScreen` draws a `SelectState` and `selectReducer` moves it; this module sends the requests, turns
 * each answer into an event and focuses the list or the first name as a move asks.
 */

/**
 * What the flow needs from the login flow: the account origin, the refresh token the login answered, the
 * account's family name and its roster, the confirmation before logging out everywhere, the page's confirm
 * dialog, which Log out asks in first, the dialog for a logout that did not finish, the looks the screen draws,
 * where the session goes once a character is chosen or created, and where the flow goes back to log in, with the
 * gateway's sentence when there is one to show.
 */
export interface SelectFlowProps {
  readonly base: string;
  readonly refreshToken: string;
  readonly familyName: string;
  readonly roster: Roster;
  readonly confirmEverywhere: ConfirmLogoutEverywhere;
  readonly ask: Ask;
  readonly retryLogout: RetryLogout;
  readonly looks: EntryLooks;
  readonly onSession: (session: Session) => void;
  readonly onReturned: (text: string | null) => void;
}

/**
 * The screen and the requests behind it. While a request is out the flow refuses another, a log out, Log out
 * everywhere, Create a character and Back; `waiting` answers that at once, before React has drawn the busy
 * button, so a second click in the same frame sends nothing.
 *
 * @param props The login flow's half of character select.
 * @returns The screen.
 */
export function SelectFlow({ base, refreshToken, familyName, roster, confirmEverywhere, ask, retryLogout, looks, onSession, onReturned }: SelectFlowProps): ReactElement {
  const [state, dispatch] = useReducer(selectReducer, null, (): SelectState => initialSelectState(familyName, roster.characters, roster.limit));
  const waiting = useRef(false);
  const firstName = useRef<HTMLInputElement>(null);
  const offer = useMemo(() => creatorOffer(looks.looks), [looks.looks]);
  const source = useLookSource(looks);
  const { images, arrivals } = useSharedImages(source);

  useEffect(() => {
    if (state.focus === "firstName") {
      firstName.current?.focus();
    } else if (state.focus === "list") {
      document.querySelector<HTMLElement>("[data-select='form'] [role='option'][tabindex='0']")?.focus();
    }
  }, [state]);

  /**
   * Plays a character: a refresh naming it, which answers the session for it. A refusal of the refresh token returns
   * to log in with the gateway's sentence; any other refusal stays on the screen with its sentence.
   *
   * @param id The character, or null when none is selected.
   */
  const enter = (id: string | null): void => {
    if (waiting.current || id === null) {
      return;
    }
    waiting.current = true;
    dispatch({ type: "chose", id });
    dispatch({ type: "sent", text: say("account.select.busy") });
    play(id);
  };

  /**
   * The refresh that chooses a character, whose answer is the session for it. A choice refused straight after the
   * page's own leave, since the character it left has not quite gone, is asked once more after a short wait while
   * the button stays busy (`chooseAfterOwnLeave`), and only the second refusal shows.
   *
   * @param id The character.
   */
  const play = (id: string): void => {
    void chooseAfterOwnLeave(() => chooseCharacter(base, refreshToken, id), { leftAt: ownLeaveAt(), now: Date.now, wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)) }).then((outcome) => {
      if (outcome.ok) {
        onSession(outcome.session);
        return;
      }
      waiting.current = false;
      if (outcome.lost) {
        storeGrant(null);
        onReturned(outcome.message);
        return;
      }
      dispatch({ type: "refused", text: outcome.message });
    });
  };

  /**
   * The create step's Continue and the Look step's Enter the world. On the create step, a first name that breaks the
   * rule is refused before anything is sent (the form is `noValidate`), and one that keeps it opens the Look step on
   * a look the creator picks, or the one chosen before. On the Look step it creates the character with the first
   * name, the voice and the look: the answer is the session for the new character, which the page plays; a refused
   * first name returns to the create step with the look kept; a refusal at the limit returns to the list with the
   * gateway's sentence; a refusal of the refresh token returns to log in with it; any other stays on the Look step.
   */
  const create = (): void => {
    if (waiting.current) {
      return;
    }
    if (state.mode === "create") {
      if (firstNameRefused(state)) {
        dispatch({ type: "invalid" });
        return;
      }
      dispatch({ type: "looking", look: creatorStart(offer, null, pageSeed()) });
      return;
    }
    if (state.mode !== "look") {
      return;
    }
    waiting.current = true;
    dispatch({ type: "sent", text: say("account.naming.busy") });
    const look = state.look;
    void createCharacter(base, refreshToken, state.firstName.trim(), state.voice, look).then((outcome) => {
      if (outcome.ok) {
        onSession(look === null ? outcome.session : { ...outcome.session, look });
        return;
      }
      waiting.current = false;
      if (outcome.lost) {
        storeGrant(null);
        onReturned(outcome.message);
        return;
      }
      dispatch(outcome.refused === "limit" ? { type: "full", text: outcome.message } : outcome.refused === "characterName" ? { type: "nameRefused", text: outcome.message } : { type: "refused", text: outcome.message });
    });
  };

  /**
   * Change look's Confirm: posts the look and the voice shown for the character, unless neither differs from what it
   * stands in. An accepted change lists the character in them; a session that is gone returns to log in; any other
   * refusal stays in the window with its sentence.
   */
  const confirmChange = (): void => {
    const change = state.change;
    if (waiting.current || state.mode !== "change" || change === null || !changeDiffers(change)) {
      return;
    }
    waiting.current = true;
    dispatch({ type: "sent", text: say("look.change.saving") });
    void changeLook(base, refreshToken, change.id, change.look, change.voice).then((outcome) => {
      waiting.current = false;
      if (outcome.ok) {
        dispatch({ type: "changed", id: change.id, look: change.look, voice: change.voice });
        return;
      }
      if (outcome.why === "lost") {
        storeGrant(null);
        onReturned(null);
        return;
      }
      dispatch({ type: "refused", text: lookRefusal(outcome).sentence });
    });
  };

  /**
   * Log out: asks first (ADR-061's amendment "character select as redesigned"), then ends the session and returns to
   * log in, once the logout has finished or the player has left it unfinished through the retry dialog. While the
   * question is open the flow refuses every other request; a cancel leaves the screen as it was.
   */
  const leave = (): void => {
    if (waiting.current) {
      return;
    }
    waiting.current = true;
    void ask(logOutQuestion()).then((confirmed) => {
      if (!confirmed) {
        waiting.current = false;
        return;
      }
      void logOutWithRetry(base, refreshToken, retryLogout).then(() => {
        waiting.current = false;
        onReturned(null);
      });
    });
  };

  /**
   * Log out everywhere: asks through the confirm dialog and, once every session has ended, returns to log in
   * as Log out does. When the gateway answers that this session is already gone, the screen returns to log in
   * with the gateway's sentence once the player has closed the dialog.
   */
  const everywhere = (): void => {
    if (waiting.current) {
      return;
    }
    let lost: string | null = null;
    void confirmEverywhere(async () => {
      const outcome = await logoutEverywhere(base, refreshToken);
      if (!outcome.ok && outcome.lost) {
        lost = outcome.message;
        storeGrant(null);
      }
      return outcome;
    }).then((ended) => {
      if (!ended && lost === null) {
        return;
      }
      onReturned(ended ? null : lost);
    });
  };

  /**
   * Preview: plays a sample of the voice picked, the new character's on the Look step and the change's in Change
   * look, or says that it has none.
   */
  const preview = (): void => {
    const voice = state.mode === "change" ? (state.change?.voice ?? null) : state.voice;
    if (!pageSound.previewVoice(voice)) {
      dispatch({ type: "told", text: say("account.voice.noSample") });
    }
  };

  /**
   * Back, unless a request is out.
   */
  const back = (): void => {
    if (!waiting.current) {
      dispatch({ type: "back" });
    }
  };

  const note = state.busy ? null : state.note;
  if (state.mode === "look" && state.look !== null) {
    return (
      <LookStep
        entry={looks}
        words={lookStepWords()}
        backGlyph="chevron-left"
        look={state.look}
        voice={state.voice}
        saving={state.busy}
        note={note?.tone === "info" ? note.text : null}
        refusal={note?.tone === "error" ? { sentence: note.text, row: null } : null}
        onLook={(look) => dispatch({ type: "looked", look })}
        onVoice={(voice) => dispatch({ type: "voiced", voice })}
        onPreview={preview}
        onBack={back}
        onPrimary={create}
      />
    );
  }
  const changing = state.change;
  const changed = state.characters.find((character) => character.id === changing?.id);
  if (state.mode === "change" && changing !== null && changed !== undefined) {
    return (
      <LookStep
        entry={looks}
        words={{ title: say("look.change.title", { name: characterLabel(changed, state.familyName) }), back: say("look.change.cancel"), randomise: say("look.change.randomise"), primary: say("look.change.confirm"), saving: say("look.change.saving") }}
        look={changing.look}
        voice={changing.voice}
        saving={state.busy}
        primaryDisabled={!changeDiffers(changing)}
        note={note?.tone === "info" ? note.text : null}
        refusal={note?.tone === "error" ? { sentence: note.text, row: null } : null}
        onLook={(look) => dispatch({ type: "looked", look })}
        onVoice={(voice) => dispatch({ type: "voiced", voice })}
        onPreview={preview}
        onBack={back}
        onPrimary={confirmChange}
      />
    );
  }
  return (
    <SelectScreen
      state={state}
      firstName={firstName}
      figures={source === null || images === null ? null : { source, images, arrivals }}
      onChoose={(id) => dispatch({ type: "chose", id })}
      onEnter={enter}
      onCreate={() => {
        if (!waiting.current) {
          dispatch({ type: "creating" });
        }
      }}
      onChangeLook={() => {
        const chosen = chosenCharacter(state);
        if (!waiting.current && chosen !== null) {
          dispatch({ type: "changing", look: creatorStart(offer, chosen.look ?? null, pageSeed()) });
        }
      }}
      onType={(value) => dispatch({ type: "typed", value })}
      onSubmitName={create}
      onBack={back}
      onLogOut={leave}
      onLogOutEverywhere={everywhere}
    />
  );
}
